<template>
  <div class="authWrapper new-login-wrapper">
    <div class="container">
      <div class="row whiteBG-wrap login-white-wrap">
        <div class="col-lg-7 col-md-7 no-padding">
          <div class="login-left-wrap">

            <!-- Back button -->
            <router-link to="/verify-identity" class="text-link">
              <h6 class="login-highlight">Back</h6>
            </router-link>

            <!-- Logo -->
            <div class="left-login d-flex">
              <div class="rep-icon-login">
                <img src="../../../assets/images/group-icon.svg" alt="Company Logo" />
              </div>
            </div>

            <!-- Title -->
            <h4 class="login-title  mb-2">Verify OTP</h4>

            <!-- OTP Form -->
            <div class="auth-form pt-1 pb-3">
              <form @submit.prevent="verifyToken">
                <label class="opt-label  mb-2 d-block">
                  Enter OTP<span class="required-indc"> *</span>
                </label>

                <!-- OTP Inputs -->
                <div class="otp-input-wrapper d-flex">
                  <input
                    v-for="(_, index) in 6"
                    :key="index"
                    ref="inputs"
                    v-model="digits[index]"
                    type="tel"
                    maxlength="1"
                    autocomplete="one-time-code"
                    class="otp-input"
                    :class="{ 'error-border': otpErrorFlag }"
                    :disabled="isLockedOut"
                    @input="handleInput(index, $event)"
                    @keydown.delete="handleDelete(index, $event)"
                    @paste.prevent="handlePaste"
                    @focus="clearError"
                  >
                </div>

                <!-- Error Message -->
                <div v-if="otpErrorFlag" class="valid-error text-center mt-2">
                  {{ errorMsg }}
                </div>

                <!-- Submit Button -->
                <div class="text-center mt-3">
                  <button 
                    type="submit"
                    class="submit-btn next-btn"
                    :disabled="!isOtpComplete || formLoader || isLockedOut"
                  >
                    <b-spinner v-if="formLoader" small />
                    {{ formLoader ? 'Verifying...' : 'Verify OTP' }}
                  </button>
                </div>
              </form>

              <!-- OTP Resend Component -->
              <login-otp 
                v-if="!isLockedOut"
                @sendOTP="sendOTP" 
                :expirySecond="60" 
                :setSpinnerLoader="otpLoader" 
              />
              
              <!-- Lockout Timer -->
              <lockout-timer 
                v-if="isLockedOut"
                :lockout-seconds="lockoutSeconds"
                @lockout-expired="handleLockoutExpired"
              />
            </div>
          </div>
        </div>

        <!-- Carousel (Hidden on mobile) -->
        <div class="col-lg-5 col-md-5 no-padding d-none d-md-block">
          <login-in-carsoule />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import helper from "@/resource/Helper";
import Logincarsoule from "./Logincarsoule";
import NewLoginOtp from "./NewLoginOtp.vue";
import LockoutTimer from "./OtpLockoutTimer.vue"; 
import axios from "axios";

export default {
  name: "OtpVerify",
  components: {
    'login-in-carsoule': Logincarsoule,
    'login-otp': NewLoginOtp,
    'lockout-timer': LockoutTimer 
  },
  data: () => ({
    digits: Array(6).fill(''),
    userEmail: '',
    formLoader: false,
    otpLoader: false,
    otpErrorFlag: false,
    errorMsg: '',
    isLockedOut: false, 
    lockoutSeconds: 0 
  }),
  computed: {
    ...mapGetters(['verifiedIdentitySelectedInfo', 'deviceId', 'groupEmail']),
    isOtpComplete() {
      return this.digits.every(d => d.length === 1);
    },
    otp() {
      return this.digits.join('');
    }
  },
  mounted() {
    if(this.$store.getters.loginOtpSource=== 'login') {
      this.userEmail = this.$store.getters.groupEmail;
    }
    if(this.$store.getters.loginOtpSource === 'forgot') {
      this.userEmail = this.$store.getters.verifiedIdentitySelectedInfo.email;
    }
    this.focusFirstInput();
  },
  methods: {
    // Input handling
    handleInput(index, event) {
      if (this.isLockedOut) return; 
      
      const value = event.target.value.replace(/\D/g, '');
      this.digits.splice(index, 1, value.slice(-1));
      
      if (value && index < 5) {
        this.$refs.inputs[index + 1].focus();
      }
      
      if (this.isOtpComplete) {
        this.verifyToken();
      }
    },

    handleDelete(index) {
      if (this.isLockedOut) return; 
      
      if (index > 0 && !this.digits[index]) {
        this.$refs.inputs[index - 1].focus();
      }
    },

    // Paste handling
    handlePaste(event) {
      if (this.isLockedOut) return;
      
      const pasteData = (event.clipboardData || window.clipboardData)
        .getData('text/plain')
        .replace(/\D/g, '')
        .slice(0, 6)
        .split('');

      this.digits = [...pasteData, ...Array(6 - pasteData.length).fill('')];
      this.focusInput(pasteData.length);
      if (this.isOtpComplete) {
        this.verifyToken();
      }
    },

    // Verification
    async verifyToken() {
      if (!this.isOtpComplete) return;
      if (this.isLockedOut) return; 

      this.formLoader = true;
      this.clearError();

      try {
        const deviceId = this.deviceId || this.$uuid.v1();
        if (!this.deviceId) this.$store.commit("setDeviceId", deviceId);
        if (this.userEmail) this.$store.commit("setGroupEmail", this.userEmail);

        const { data } = await axios.post(
          `${process.env.VUE_APP_API_URL_V3}auth/reset-verify-otp`,
          {
            email: this.userEmail,
            device_id: deviceId,
            reset_code: this.otp
          }
        );

        if (data.status === "success") {
          this.$store.commit("setOtpCode", data.reset_token);
          this.$router.push("/password-reset");
        }
      } catch (error) {
        this.handleVerificationError(error);
      } finally {
        this.formLoader = false;
      }
    },

    // Helpers
    focusFirstInput(retry = 5) {
      this.$nextTick(() => {
        const input = this.$refs.inputs?.[0];
        if (input) {
          input.focus();
        } else if (retry > 0) {
          setTimeout(() => this.focusFirstInput(retry - 1), 50);
        }
      });
    },
    
    focusInput(index) {
      this.$nextTick(() => {
        const targetIndex = Math.min(index, 5);
        this.$refs.inputs[targetIndex]?.focus();
      });
    },

    clearError() {
      this.otpErrorFlag = false;
      this.errorMsg = '';
    },

    // ADDED: Parse lockout duration from error message
    parseLockoutDuration(message) {
      const match = message.match(/(\d+)\s*minute[s]?(\s*and\s*(\d+)\s*second[s]?)?|(\d+)\s*second[s]?/);
      if (!match) {
        console.warn('Failed to parse lockout duration:', message);
        return 60; // Default to 60 seconds if parsing fails
      }
      const minutes = parseInt(match[1]) || 0;
      const seconds = parseInt(match[3]) || parseInt(match[4]) || 0;
      return minutes * 60 + seconds;
    },

    // ADDED: Handle verification errors including lockout
    handleVerificationError(error) {
      const errorResponse = error.response?.data;
      
      // Handle 429 Lockout
      if (errorResponse?.statusCode === 429 || error.response?.status === 429) {
        this.isLockedOut = true;
        this.lockoutSeconds = this.parseLockoutDuration(errorResponse?.message || '');
        this.otpErrorFlag = false;
        this.errorMsg = '';
      } 
      // Handle other errors
      else {
        this.otpErrorFlag = true;
        this.errorMsg = errorResponse?.message || 'Verification failed. Please try again.';
      }
    },

    // ADDED: Handle lockout expiration
    handleLockoutExpired() {
      this.isLockedOut = false;
      this.lockoutSeconds = 0;
      this.otpErrorFlag = false;
      this.errorMsg = '';
      this.digits = Array(6).fill('');
      this.focusFirstInput();
    },

    // Resend OTP
    async sendOTP() {
      if (this.isLockedOut) return; 
      
      this.otpLoader = true;
      try {
        const { data } = await axios.post(
          `${process.env.VUE_APP_API_URL_V3}auth/reset-generate-otp`,
          this.verifiedIdentitySelectedInfo
        );
        
        if (data.status === "success") {
          helper.infoMessage(this, data.status, data.message);
          this.clearError();
          this.digits = Array(6).fill('');
          this.focusFirstInput();
        }
      } catch (error) {
        const errorResponse = error.response?.data;
        
        // Handle 429 Lockout in resend
        if (errorResponse?.statusCode === 429 || error.response?.status === 429) {
          this.isLockedOut = true;
          this.lockoutSeconds = this.parseLockoutDuration(errorResponse?.message || '');
          this.otpErrorFlag = false;
          this.errorMsg = '';
        } 
        // Handle other errors
        else {
          helper.infoMessage(
            this,
            errorResponse?.status || 'error',
            errorResponse?.message || 'Failed to send OTP'
          );
        }
      } finally {
        this.otpLoader = false;
      }
    }
  }
};
</script>

<style scoped>
/* OTP Input Styling */
.otp-input-wrapper {
  gap: 25px;
  margin-bottom: 10px;
}

.auth-form .otp-input-wrapper input.otp-input {
  height: 53px ;
  border: 1px solid #bbb ;
  border-radius: 8px ;
  text-align: center ;
  font-size: 1.2rem ;
  transition: all 0.2s ease;
  box-shadow: 0;
}

.otp-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
  outline: none;
}

.error-border {
  border-color: #dc3545 !important;
}

/* Responsive Design */
@media (max-width: 576px) {
  .otp-input {
    width: 2.5rem;
    height: 3rem;
    font-size: 1rem;
  }
  
  .submit-btn {
    width: 100%;
    padding: 0.75rem;
  }
}

/* Submit Button */
.submit-btn {
  background: #007bff;
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.submit-btn:not(:disabled):hover {
  background: #0056b3;
}

/* Error Message */
.valid-error {
  color: #dc3545;
  font-size: 0.875rem;
  min-height: 1.25rem;
}
.otp-separator {
  display: inline-block;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 6px;
  color: #666;
}
@media(max-width: 1399px){
  .otp-input-wrapper {
    gap: 15px;
  }
}
@media (max-width: 992px) {
    .otp-input-wrapper {
        gap: 5px;
        margin: 0 0 10px 0;
    }
}
</style>